import { Component, Input, OnChanges, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { QuestionBase } from '@jump-tech-frontend/domain';
import { I18nKeys } from '@jump-tech-frontend/app-config';
import { BaseQuestionComponent } from '../question.component';
import { ScrollService } from '../scroll.service';
import { untilDestroyed } from '@jump-tech-frontend/angular-common';
import { QuestionHintComponent } from '@jump-tech-frontend/question-components';
import { QuestionLabelContentComponent } from '@jump-tech-frontend/question-components';

import { JUI_CHECKBOX_IMPORTS } from '@jump-tech-frontend/core-components-angular';

@Component({
  selector: `crds-question-toggle`,
  template: `
    @if (show && form) {
      <div [formGroup]="form" class="form-group form-inline">
        @if (useJuiControls) {
          <div>
            <jui-checkbox
              toggle="true"
              [label]="question.label"
              [toggleontext]="i18ns.yes"
              [toggleofftext]="i18ns.no"
              [formControlName]="question.key"
              [size]="controlSize"
            ></jui-checkbox>
          </div>
        } @else {
          @if (question.label) {
            <label class="form-label" [attr.for]="question.key" [attr.data-qa]="question.key">
              <question-label-content [question]="question" [isInvalid]="isInvalid" [i18ns]="i18ns"></question-label-content>
            </label>
          }
          <label class="switch">
            <input [id]="question.key" type="checkbox" [formControlName]="question.key" #toggleInput />
            <span class="slider round" [attr.data-qa]="question.key + '_click'"></span>
            <span class="toggleValueUserDisplay"></span>
          </label>
          <span [attr.data-qa]="question.key + '_value'">{{ form.get(question.key).value ? i18ns.yes : i18ns.no }}</span>
        }
        <!-- QUESTION HINT -->
        <question-hint [question]="question"></question-hint>
      </div>
    }
    `,
  imports: [
    ReactiveFormsModule,
    QuestionLabelContentComponent,
    QuestionHintComponent,
    ...JUI_CHECKBOX_IMPORTS
]
})
export class ToggleQuestionComponent extends BaseQuestionComponent implements OnChanges {
  @Input() override form: UntypedFormGroup;
  @Input() override question: QuestionBase<any>;
  @Input() i18ns: I18nKeys;
  @Input() useJuiControls? = false;
  @Input() deviceContext?: string = 'desktop';
  @ViewChild('toggleInput') input;
  type: string;
  controlSize = 'md';

  private untilDestroyed = untilDestroyed();

  constructor(private renderer: Renderer2, private scrollService: ScrollService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.controlSize = this.getControlSize();
    if (!this.question || !Object.prototype.hasOwnProperty.call(changes, 'question')) {
      return;
    }

    this.scrollService.scrollObservable.pipe(this.untilDestroyed()).subscribe(documentId => {
      if (documentId === this.question.key) {
        this.input.nativeElement.scrollIntoView();
      }
    });
  }

  get isInvalid() {
    return this.form?.get(this.question.key)?.touched && this.form?.get(this.question.key)?.invalid;
  }

  get show() {
    const showIfQuestionPopulated = this.question.showIf ? this.showIf : true;
    return (
      (this.question.showIfPopulated !== true || this.value) && showIfQuestionPopulated && this.question.show !== false
    );
  }

  getControlSize() {
    return this.deviceContext === 'mobile' ? 'lg' : 'md';
  }
}
